import ColorUtils from 'color'
import { CssVarProperties, getCssVar } from 'lib/css/CssVarProperties'
import { Optional } from 'lib/types'
import { colors } from 'parameters'
import { ColorRanges } from './types'
import { removeUndefValues } from 'lib/objects/removeUndefineds'
import { css } from '@emotion/react'

export type ColorFamilyProps = { color: Color }

interface Options {
	lighter: string
	darker: string
	onColor: string
}

/**
 * Loosly inspired by https://m3.material.io/styles/color/the-color-system/color-roles
 *
 */
export class Color {
	base: string
	/**
	 * Used for emphasis or extra contrast, plays the role of 'PrimaryContainer'
	 * @type {string}
	 */
	lighter: string
	/**
	 * Used for emphasis or extra contrast, plays the role of 'OnPrimaryContainer'
	 * @type {string}
	 */
	darker: string
	/**
	 * The color of text that should be used for foreground objects https://m3.material.io/styles/color/the-color-system/color-roles
	 * @type {Optional<string>}
	 */
	onColor: Optional<string>

	constructor(base: string, { lighter, darker, onColor }: Partial<Options> = {}) {
		const color = ColorUtils(base)
		this.base = color.hex()
		this.lighter = lighter ?? color.lightness(90).hex()
		this.darker = darker ?? color.darken(0.3).hex()
		this.onColor = onColor ?? ((color.isDark() && colors.white) || undefined)
	}

	toMod() {
		return css({ ...this.toCssVars() })
	}

	toCssVars(): CssVarProperties {
		return removeUndefValues({
			'--color-on': this.onColor,
			'--color-lighter': this.lighter,
			'--color-base': this.base,
			'--color-darker': this.darker
		})
	}
}

export const getColorFromRange = (range: ColorRanges<number>, value: number) =>
	range[
		Object.keys(range)
			.sort()
			.reduce((highest, next) => (value <= highest ? highest : Number(next)), 0)
	]

export const ColorProperties = {
	Lighter: getCssVar('--color-lighter'),
	Darker: getCssVar('--color-darker'),
	Base: getCssVar('--color-base'),
	On: getCssVar('--color-on')
}
